import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { EmptyState, SearchInput } from "../../components/admin/AdminUI";
import { batchLabel, errorMessage, handleError } from "./shared";
import ApplyPreviewModal from "./ApplyPreviewModal";

const PAGE_SIZE = 50;

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Has pending items" },
  { key: "complete", label: "Complete" },
  { key: "not_initialized", label: "No records yet" },
];

// Daftar student + ringkasan pembagian paket. Filter & paginasi dikerjakan
// server (GET /student-package/students).
const PackageDistribution = ({ meta, refreshKey, onOpen }) => {
  const navigate = useNavigate();
  const [batch, setBatch] = useState(meta.runningBatch || "all");
  const [program, setProgram] = useState("all");
  const [status, setStatus] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [applyOpen, setApplyOpen] = useState(false);
  const [reload, setReload] = useState(0);

  // Pencarian ditunda sebentar supaya tidak memanggil server di setiap ketukan.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => setPage(1), [batch, program, status, search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get("/student-package/students", { params: { batch, program, status, search, page, limit: PAGE_SIZE } })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) return handleError(err, navigate);
        setError(errorMessage(err, "Unable to load the students."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [batch, program, status, search, page, refreshKey, reload, navigate]);

  const counts = data ? data.counts : {};
  const pageCount = data ? Math.max(Math.ceil(data.total / PAGE_SIZE), 1) : 1;
  const hasFilter = program !== "all" || search || status !== "all";

  return (
    <>
      <div className="adm-toolbar" style={{ flexWrap: "wrap" }}>
        <select className="form-select" style={{ width: "auto", fontWeight: 600 }} value={batch} onChange={(e) => setBatch(e.target.value)} aria-label="Batch">
          {meta.batches.map((b) => (
            <option key={b} value={b}>
              {batchLabel(b)}
              {b === meta.runningBatch ? " — running" : ""}
            </option>
          ))}
          <option value="all">All batches</option>
        </select>
        <SearchInput value={searchInput} onChange={setSearchInput} placeholder="Search name or student ID" />
        <select className="form-select" style={{ width: "auto" }} value={program} onChange={(e) => setProgram(e.target.value)} aria-label="Program">
          <option value="all">All programs</option>
          {meta.programs.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        {hasFilter && (
          <button
            type="button"
            className="adm-btn adm-btn--ghost"
            onClick={() => {
              setProgram("all");
              setSearchInput("");
              setStatus("all");
            }}
          >
            Reset filters
          </button>
        )}
        <span className="adm-toolbar-spacer" />
        {batch !== "all" && (
          <button type="button" className="adm-btn adm-btn--secondary" onClick={() => setApplyOpen(true)}>
            <i className="bi bi-arrow-repeat"></i> Apply batch items to students
          </button>
        )}
      </div>

      <div className="adm-tabs" role="tablist" aria-label="Distribution status">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={status === t.key}
            className={`adm-tab${status === t.key ? " adm-tab--active" : ""}`}
            onClick={() => setStatus(t.key)}
          >
            {t.label}
            {data && <span className="adm-tab-count">{counts[t.key] ?? 0}</span>}
          </button>
        ))}
      </div>

      <div className="adm-card adm-card--flush">
        {error ? (
          <EmptyState icon="bi-exclamation-triangle" title="Unable to load" text={error} />
        ) : loading && !data ? (
          <EmptyState icon="bi-hourglass" title="Loading students…" />
        ) : data.students.length === 0 ? (
          <EmptyState
            icon="bi-box-seam"
            title="No students to show"
            text={
              status === "not_initialized" || hasFilter
                ? "No student matches the current filter."
                : "No student matches this batch. Try another batch or All batches."
            }
          />
        ) : (
          <>
            <div className={`adm-table-wrap${loading ? " spk-loading" : ""}`}>
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Batch</th>
                    <th>Program</th>
                    <th>Collection</th>
                    <th style={{ minWidth: 170 }}>Progress</th>
                    <th>Pending</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((s) => {
                    const pct = s.total ? Math.round((s.received / s.total) * 100) : 0;
                    return (
                      <tr key={s.studentRowId} className="spk-row" onClick={() => onOpen(s.studentRowId)}>
                        <td>
                          <div className="adm-cell-title">{s.name}</div>
                          <div className="adm-cell-meta">{s.studentId}</div>
                        </td>
                        <td>{s.batch || "—"}</td>
                        <td>{s.program}</td>
                        <td>{s.deliveryMethod || <span className="spk-dim">—</span>}</td>
                        <td>
                          {s.total === 0 ? (
                            <span className="spk-dim">No records yet</span>
                          ) : (
                            <div className="spk-progress">
                              <div className="adm-progress" style={{ flex: 1 }}>
                                <div className="adm-progress-bar" style={{ width: `${pct}%` }} />
                              </div>
                              <span>
                                {s.received}/{s.total}
                              </span>
                            </div>
                          )}
                        </td>
                        <td>
                          {s.total === 0 ? (
                            "—"
                          ) : s.pending === 0 ? (
                            <span className="adm-status adm-status--success">Complete</span>
                          ) : (
                            <span className="adm-status adm-status--warning">{s.pending} pending</span>
                          )}
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="adm-btn adm-btn--secondary adm-btn--sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpen(s.studentRowId);
                            }}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="adm-table-footer spk-pager">
              <span>
                {(data.page - 1) * PAGE_SIZE + 1}–{Math.min(data.page * PAGE_SIZE, data.total)} of {data.total} students
              </span>
              {pageCount > 1 && (
                <div className="d-flex gap-2">
                  <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
                    Previous
                  </button>
                  <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" disabled={page >= pageCount || loading} onClick={() => setPage(page + 1)}>
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {applyOpen && <ApplyPreviewModal batch={batch} onClose={() => setApplyOpen(false)} onApplied={() => setReload((n) => n + 1)} />}
    </>
  );
};

export default PackageDistribution;
